import { useNavigate } from 'react-router-dom'

import { AvailableModules } from 'generated/featureDefinitions'
import { ModulesCrossSellingModule, ModulesDatoModule } from 'generated/resources/datatable.graphql'
import { CanSelfUpsellDocument, OneWaitlistDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import type { Navigation, NavigationPaths } from 'modules/core/lib/navigation/context'

import { useMarketplaceModulesPath } from 'modules/marketplace_modules/hooks/useMarketplaceModulesPath'
import { moduleAvailableToModuleId } from 'modules/upsell/lib/moduleIcons'

import { CrossSellingModalState } from './types'

/**
 * Builds the cross selling object for the dropdown.
 */
export function BuildCrossSelling({
  moduleId,
  crossSellingModules,
  setCrossSellingModal,
  crossSellingVisible,
  setCrossSellingVisible,
  setViewUpsellProductCardComponent,
  navigation,
  companyId,
}: {
  moduleId: string | undefined
  crossSellingModules: (ModulesCrossSellingModule | ModulesDatoModule)[] | undefined
  setCrossSellingModal: (open: CrossSellingModalState) => void
  crossSellingVisible: boolean
  setCrossSellingVisible: (open: boolean) => void
  setViewUpsellProductCardComponent: (date: string) => void
  navigation: Navigation<NavigationPaths>
  companyId: number
}) {
  const navigate = useNavigate()
  const { getListPath } = useMarketplaceModulesPath()
  const { data: selfUpsellPolicies } = useQuery(CanSelfUpsellDocument)
  const canSelfUpsell = selfUpsellPolicies?.billing._policies.subscriptionUpgradeSubscription.can
  const { data: oneWaitlistData } = useQuery(OneWaitlistDocument, {
    variables: {
      companyId: companyId,
    },
  })
  const canJoinOneWaitlist = oneWaitlistData?.upsell._policies.canJoinOneWaitlist.can
  const alreadyInOneWaitlist =
    oneWaitlistData?.companies.featureAvailabilitiesConnection.nodes[0]?.available === true

  const crossSelling = {
    isVisible: crossSellingVisible,
    sectionTitle: i18n.t('product_updates.notification_widget.cross_selling_section_title'),

    onClose: () => {
      setViewUpsellProductCardComponent(new Date().toISOString())
      setCrossSellingVisible(false)
      track(trackingEvents.upsell.dismissProductUpsellComponent, {
        source: 'product_updates_dropdown',
        component: 'product_card',
        module: moduleId,
      })
    },
    products: [
      // Marketplace
      ...(canSelfUpsell
        ? [
            {
              title: i18n.t('product_updates.notification_widget.marketplace_product_title'),
              description: i18n.t(
                'product_updates.notification_widget.marketplace_product_description'
              ),
              onClick: () => {
                track(trackingEvents.marketplaceModules.goToModulesPage, {
                  source: 'Discoverability Widget - Marketplace',
                })
                navigate(getListPath())
              },
              dismissable: false,
              module: moduleAvailableToModuleId('discover'),
              trackVisibility: (open: boolean) => {
                if (open) {
                  track(trackingEvents.marketplaceModules.viewModulesPageAccess, {
                    source: 'Discoverability Widget - Marketplace',
                  })
                }
              },
            },
          ]
        : []),
      // Existing cross-selling modules
      ...(crossSellingModules ?? []).map((crossSellingModule) => ({
        title: crossSellingModule.name || '',
        description: crossSellingModule.shortDescription || '',
        onClick: () => {
          setCrossSellingModal({
            isOpen: true,
            // eslint-disable-next-line no-type-assertion/no-type-assertion
            moduleRequested: crossSellingModule.chargebeeModuleName as AvailableModules,
          })
          track(trackingEvents.upsell.clickProductUpsellComponent, {
            source: 'product_updates_dropdown',
            module: crossSellingModule.chargebeeModuleName,
            component: 'product_card',
          })
        },
        dismissable: false,
        module: moduleAvailableToModuleId(crossSellingModule.chargebeeModuleName || ''),
        trackVisibility: (open: boolean) => {
          if (open) {
            track(trackingEvents.upsell.viewProductUpsellComponent, {
              source: 'product_updates_dropdown',
              module: crossSellingModule.chargebeeModuleName,
              component: 'product_card',
            })
          }
        },
      })),
    ],
  }

  return crossSelling
}
